/*

  Action creators which conceptually "save" data can use this system to 
  track save state.

  A "save flow" is any action where client data is persisted to the 
  backend. A save flow utilizes the below functions (and associated
  reducers/selectors) to track its state. 

  A "save flow" identifies itself with an `id`.

  A "save flow" may be "pending", "blocking" or "sending" (i.e., "committing").

  A "save flow" contains all the complexity for performing pending and blocking
  logic and merely utilizes these functions for state tracking.

  A "save flow" may produce many "save actions" which are the actual 
  XHR requests for communicating state to the backend. A "save action"
  is identified by a "saveId" which should be unique for every new save action.

  A "save action" can be "sending" or "failed". (Once a save action
  is committed, it is deleted.)

*/


import uniqueId from 'lodash/uniqueId';

// Due to our two different JS envs (exterior editor, in page editor)
// its possible to end up with duplicate unique IDs! Ooops!
// As such we append a global level uniqueness to the save prefix
const HACKY_JS_UNIVERSE_ID = Math.floor(Math.random()*1000000);
const SAVE_ID_PREFIX = `save-${HACKY_JS_UNIVERSE_ID}-`;


/**
  Return a unique save id suitable to identify a save action
  for a save flow
*/
export const makeSaveId = ()=>
  uniqueId(SAVE_ID_PREFIX);


/**
  
  Mark app state identified by `id` as "pending" a save (i.e., dirty). 

  (Note the save system has no knowledge of what part of state is dirty, only
  that the caller has identified it with `id`, and is performing its own
  save flow.)

  - `id` - uniquely identifying a save flow, really only relevant for throttling
  - `descriptor` - human readable description of this save flow
  - `data` - arbitrary data items to be stored and returned via `getPendingSave`

*/
export const pendSave = (id, descriptor, data)=> {
  return {
    type: 'PEND_SAVE',
    id,
    descriptor,
    data
  };
};

/**
  
  Inform the saving system that the app state identified by `id` is in flight
  to be stored by backend.

  - `id` - uniquely identifies a save flow
  - `saveId` - arbitrary string (probably from `makeSaveId`) which identifies this particular
    commit/persist action (probably PATCH to rest api).
 
*/
export const sendSave = (id, saveId)=> {
  return {
    type: 'SEND_SAVE',
    id,
    saveId
  };
};

/**

  Shortcut function combining merging `pendSave` and `sendSave` into one.

  This is used to track un-throttled saves. 

*/
export const startSave = (id, saveId, descriptor, data)=>
  (dispatch)=> {
    dispatch(pendSave(id, descriptor, data));
    dispatch(sendSave(id, saveId));
  };


/**

  Mark the save flow identified by `saveId` as successfully comleting its 
  backend commit/persist action.

  Optionally, mark the "next" save flow with the same `id` as this one as
  "blocked". This enables "throttling" of saves.

  (The save flow system needs to read `getPendingOrBlockingSaves` to know
  not to send the save while blocking.)

  - `saveId` - matches the previously identified `sendSave` commit/persist action
  - `blockNext` - if `true` then mark the flow `id` associated with 
    this `saveId` as blocked

*/
export const commitSave = (saveId, { blockNext=false }={})=> {
  return {
    type: 'COMMIT_SAVE',
    saveId,
    blockNext
  };
};

/**

  Mark the app state identified by `id` as ready to send, by removing the "block".
  
*/
export const unblockSave = (id)=> {
  return {
    type: 'UNBLOCK_SAVE',
    id
  };
};

/**

  Mark the safe action identified by `saveId` as having failed.

*/
export const failSave = (saveId)=> {
  return {
    type: 'FAIL_SAVE',
    saveId
  };
};
